import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import DropdownSelect, { OptionProps } from 'src/ui/wf/common/select/DropdownSelect';
import {
  disasterColumns,
  disasterCustomStyles,
  transformToDisasterRows,
} from '../DisasterRegionColumns';
import { branchSummaryColumns, branchSummaryCustomStyles } from './BranchSummaryColumns';
import { BranchTableHeader } from './BranchTableHeader';
import useWfWorkplaceService from 'src/api/wf/service/WfWorkplaceService';
import Button from 'src/ui/wf/common/button/Button';
import { wfNavigate } from 'src/ui/wf/router/WfScreenRegistration';
import { WfScreensMap } from 'src/ui/wf/router/WfScreensMap';
import { WF_Screen } from 'src/ui/wf/router/WfScreens';
import { useWfFilterStore } from 'src/api/wf/stores/WfFilterStore';
import { handleUpdateTimestamp } from 'src/ui/wf/common/util/dateUtils';
import { useWfTyphoonStore } from 'src/api/wf/stores/WfTyphoonStore';
import { WfBranchSummaryResponse } from 'src/api/wf/models/WfSummary';
import { FormRow } from 'src/ui/wf/common/form/FormRow';

export const BranchSummary = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { currentTyphoon } = useWfTyphoonStore();
  const { patchFilterParameters, patchAlertFilterParameters, filterParameters, branchOptions } =
    useWfFilterStore();
  const { fetchSummaryByBranch, fetchDisasterAreaBranches, fetchBranchOptions } =
    useWfWorkplaceService();

  const [tableData, setTableData] = useState<WfBranchSummaryResponse[]>([]);
  const [allBranchesSummary, setAllBranchesSummary] = useState<string[]>([]);
  const [lastClickedTime, setLastClickedTime] = useState<string | null>(() =>
    localStorage.getItem('last_branch_click_time')
  );

  const zigzagData = useMemo(() => transformToDisasterRows(branchOptions), [branchOptions]);

  const branchDropdownOptions = useMemo(
    (): OptionProps[] => [
      { title: t('wf.summary.all'), value: '' },
      ...branchOptions.map((branch) => ({
        title: `${branch.code} ${branch.name}`,
        value: branch.code,
      })),
    ],
    [branchOptions, t]
  );

  const selectedBranchOption = useMemo(
    () =>
      branchDropdownOptions.find((opt) => opt.value === filterParameters.branch) ||
      branchDropdownOptions[0],
    [branchDropdownOptions, filterParameters.branch]
  );

  const handleFetchData = useCallback(async () => {
    if (!currentTyphoon?.id) return;

    try {
      const finalData = await fetchSummaryByBranch(currentTyphoon.id, filterParameters.branch);
      setTableData(finalData || []);
    } catch (error) {
      console.error('Error while loading branch summary:', error);
      setTableData([]);
    }
  }, [fetchSummaryByBranch, currentTyphoon?.id, filterParameters.branch]);

  const handleBusinessLocationSelected = useCallback(
    (branchCode: string, salesOffice: string) => {
      patchFilterParameters({
        branch: branchCode,
        salesOffice,
        customerNumber: '',
      });
      patchAlertFilterParameters({
        branchCode,
        customerNumber: '',
      });
      wfNavigate(navigate, WfScreensMap[WF_Screen.BusinessSummary].path);
    },
    [navigate, patchAlertFilterParameters, patchFilterParameters]
  );

  const handleRegionalOfficeChanged = (event: ChangeEvent<HTMLSelectElement>) => {
    const branchCode = event.target.value;

    patchFilterParameters({
      branch: branchCode,
      salesOffice: '',
      section: '',
      squad: '',
      customerNumber: '',
    });
    patchAlertFilterParameters({
      branchCode,
      salesOfficeCode: '',
      sectionCode: '',
      squadCode: '',
      customerNumber: '',
    });
  };

  const handleUpdateClick = () => {
    handleUpdateTimestamp('last_branch_click_time', setLastClickedTime);
    handleFetchData();
  };

  useEffect(() => {
    handleUpdateTimestamp('last_branch_click_time', setLastClickedTime);
  }, []);

  useEffect(() => {
    if (branchOptions.length === 0) {
      fetchBranchOptions();
    }
  }, [branchOptions.length, fetchBranchOptions]);

  useEffect(() => {
    handleFetchData();
  }, [handleFetchData, currentTyphoon?.id]);

  useEffect(() => {
    const fetchHighlighting = async () => {
      if (!currentTyphoon?.id) return;
      try {
        const codes = await fetchDisasterAreaBranches(currentTyphoon.id);
        setAllBranchesSummary(codes || []);
      } catch (e) {
        console.error('Highlighting fetch failed', e);
      }
    };
    fetchHighlighting();
  }, [currentTyphoon?.id, fetchDisasterAreaBranches]);

  return (
    <div className="mx-auto flex max-w-fit flex-col gap-2 py-2">
      <div className="flex flex-col gap-2">
        <div className="flex flex-col items-center gap-4 rounded-sm border-2 border-sky-600 p-3 shadow-sm sm:flex-row">
          <div className="w-full flex-1">
            <FormRow title={t('wf.common.branchOffice')} required>
              <DropdownSelect
                selectedValue={selectedBranchOption}
                optionValues={branchDropdownOptions}
                onChange={handleRegionalOfficeChanged}
              />
            </FormRow>
          </div>
          <Button title={t('wf.common.update')} onClick={handleUpdateClick} />
          {lastClickedTime && (
            <span className="self-center text-sm text-gray-600">{lastClickedTime}</span>
          )}
        </div>
        <div className="w-full rounded-sm border-2 border-sky-600 bg-gray-50 p-3 text-center text-sm ">
          {t('wf.common.displayedContent')}
        </div>
      </div>

      <div className="flex h-fit flex-col overflow-hidden rounded-sm border border-gray-300 shadow-sm">
        <DataTable
          noDataComponent={<div className="p-4">{t('wf.common.noResults')}</div>}
          noTableHead
          columns={disasterColumns(allBranchesSummary)}
          data={zigzagData}
          customStyles={disasterCustomStyles}
          dense
        />
      </div>

      <div className="flex w-full flex-col">
        <h2 className="mb-2 text-sm font-semibold tracking-wide">{t('wf.summary.branch.title')}</h2>

        <div className="w-full overflow-x-auto rounded-sm border border-gray-300 shadow-sm">
          <div className="inline-block min-w-max align-middle">
            <BranchTableHeader t={t} />
            <DataTable
              noDataComponent={<div className="p-4">{t('wf.common.noResults')}</div>}
              noTableHead
              columns={branchSummaryColumns(handleBusinessLocationSelected)}
              data={tableData}
              customStyles={branchSummaryCustomStyles}
              dense
              pagination
              paginationPerPage={10}
              conditionalRowStyles={[totalRowStyle(filterParameters.branch ?? '')]}
            />
          </div>
        </div>
      </div>

      <Button title={t('wf.common.back')} onClick={() => navigate(-1)} className="self-start" />
    </div>
  );
};

const totalRowStyle = (branchCode: string) => ({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  when: ((_row: WfBranchSummaryResponse, index: number) => index === 0 && !branchCode) as any,
  style: {
    backgroundColor: '#f3f4f6',
    fontWeight: 'bold' as const,
    color: '#111827',
  },
});
